import React, { useEffect, useMemo, useState, useRef } from "react"
import { supabase } from "../../lib/supabaseClient"
import { haversineDistance } from "../../utils/geo"
import ProductCard from "./ProductCard"
import ProductFilters from "../Search/ProductFilters"
import CompareStrip from "../Compare/CompareStrip"

export default function PublicProductList() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [category, setCategory] = useState("")
  const [sort, setSort] = useState("price_asc")
  const [q, setQ] = useState("")
  const [location, setLocation] = useState({
    estado: "",
    municipio: "",
    parroquia: "",
  })
  const [userPos, setUserPos] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 10.4806, lng: -66.9036 })
  const [activeId, setActiveId] = useState(null)
  const [compareProducts, setCompareProducts] = useState([])

  // Cargar productos
  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("status", "Publicada")
        .order("created_at", { ascending: false })

      if (!mounted) return
      if (error) setError(error.message)
      setProducts(data || [])
      setLoading(false)
    })()
    return () => {
      mounted = false
    }
  }, [])

  // Pedir ubicación SOLO si el usuario selecciona "distance"
  useEffect(() => {
    // Intentar obtener la ubicación del usuario al montar el componente.
    // Si el usuario lo permite, calcularemos distancias para los productos.
    if (!navigator.geolocation) return
    let mounted = true
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!mounted) return
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        setUserPos(coords)
        setMapCenter(coords)
      },
      () => {
        console.warn("No se pudo obtener ubicación del usuario")
      },
      { enableHighAccuracy: true, timeout: 5000 }
    )
    return () => {
      mounted = false
    }
  }, [sort])

  // Decorar productos con distancia
  const decorated = useMemo(() => {
    if (!products?.length) return []
    return products.map((p) => {
      const withLoc = { ...p }
      // Asegurarse que las coordenadas vengan como números
      const lat = p.geo_lat != null ? Number(p.geo_lat) : null
      const lng = p.geo_lng != null ? Number(p.geo_lng) : null
      if (
        userPos &&
        Number.isFinite(userPos.lat) &&
        Number.isFinite(userPos.lng) &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      ) {
        withLoc.__distanceKm = haversineDistance(
          { lat: Number(userPos.lat), lng: Number(userPos.lng) },
          { lat, lng }
        )
      } else {
        // Si no está disponible, marcar como null para distinguir
        withLoc.__distanceKm = null
      }
      return withLoc
    })
  }, [products, userPos])

  // Filtrar y ordenar
  const filtered = useMemo(() => {
    let arr = decorated
    if (category)
      arr = arr.filter(
        (p) => (p.category || "").toLowerCase() === category.toLowerCase()
      )
    if (q) {
      const s = q.toLowerCase()
      arr = arr.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(s) ||
          (p.description || "").toLowerCase().includes(s)
      )
    }
    if (location?.estado)
      arr = arr.filter((p) =>
        (p.address || "").toLowerCase().includes(location.estado.toLowerCase())
      )
    if (location?.municipio)
      arr = arr.filter((p) =>
        (p.address || "")
          .toLowerCase()
          .includes(location.municipio.toLowerCase())
      )
    if (location?.parroquia)
      arr = arr.filter((p) =>
        (p.address || "")
          .toLowerCase()
          .includes(location.parroquia.toLowerCase())
      )

    switch (sort) {
      case "price_asc":
        arr = [...arr].sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
        break
      case "price_desc":
        arr = [...arr].sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
        break
      case "distance":
        arr = [...arr].sort(
          (a, b) => (a.__distanceKm ?? 1e9) - (b.__distanceKm ?? 1e9)
        )
        break
      case "recent":
      default:
        arr = [...arr].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
        break
    }
    return arr
  }, [decorated, category, sort, q, location])

  const focusMapTo = (p) => {
    if (p?.geo_lat && p?.geo_lng) {
      setMapCenter({ lat: p.geo_lat, lng: p.geo_lng })
      setActiveId(p.id)
    }
  }

  const addToCompare = (product) => {
    setCompareProducts((prev) => {
      if (!product) return prev
      if (prev.find((x) => x.id === product.id)) return prev
      return [...prev, product]
    })
  }

  const removeFromCompare = (productId) => {
    setCompareProducts((prev) => prev.filter((p) => p.id !== productId))
  }

  const clearCompare = () => setCompareProducts([])

  // Paginación
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 9

  // Resetear página cuando cambian filtros o resultados
  useEffect(() => {
    setCurrentPage(1)
  }, [
    category,
    q,
    location.estado,
    location.municipio,
    location.parroquia,
    sort,
  ])

  return (
    <section className="container mx-auto px-4 py-8">
      <ProductFilters
        category={category}
        setCategory={setCategory}
        sort={sort}
        setSort={setSort}
        q={q}
        setQ={setQ}
        location={location}
        setLocation={setLocation}
      />
      {loading ? (
        <p className="mt-6">Cargando…</p>
      ) : error ? (
        <p className="mt-6 text-red-600">Error: {error}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {filtered
              .slice(
                (currentPage - 1) * itemsPerPage,
                currentPage * itemsPerPage
              )
              .map((p) => (
                <ProductCard
                  key={p.id}
                  p={p}
                  onDirections={focusMapTo}
                  onAddToCart={addToCompare}
                />
              ))}
            {!filtered.length && (
              <div className="text-sm text-gray-500">
                No hay resultados con esos filtros.
              </div>
            )}
          </div>
          {/* Controles de paginación */}
          {filtered.length > itemsPerPage && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1
                    ? "text-gray-400 border-gray-200"
                    : "text-gray-700 border-gray-300"
                }`}
              >
                Anterior
              </button>

              <div className="flex items-center gap-1">
                {Array.from({
                  length: Math.ceil(filtered.length / itemsPerPage),
                }).map((_, i) => {
                  const page = i + 1
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`px-2 py-1 rounded ${
                        page === currentPage
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-200"
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
              </div>

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) =>
                    Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1)
                  )
                }
                disabled={
                  currentPage >= Math.ceil(filtered.length / itemsPerPage)
                }
                className={`px-3 py-1 rounded border ${
                  currentPage >= Math.ceil(filtered.length / itemsPerPage)
                    ? "text-gray-400 border-gray-200"
                    : "text-gray-700 border-gray-300"
                }`}
              >
                Siguiente
              </button>
            </div>
          )}
          <CompareStrip
            products={compareProducts}
            onRemove={removeFromCompare}
            onClear={clearCompare}
          />
        </>
      )}
    </section>
  )
}
